import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  adminRemoveBonus,
  adminRestoreBonus,
  banBonusUser,
  dismissBonusReport,
  fetchAdminBonusModeration,
  resolveBonusReport,
  timeoutBonusUser,
  type AdminBonusAction,
  type AdminBonusReport,
  type AdminReportedUser,
  type BonusReportStatus
} from '../api/adminBonuses';
import { displayBonusLabel } from '../api/bonuses';
import { ErrorState } from '../components/ErrorState';
import { LoadingState } from '../components/LoadingState';

type ModerationTab = 'open' | 'closed';

type ReportModerationState = {
  note: string;
  timeoutDays: number;
};

function formatDateTime(value?: string | null) {
  if (!value) {
    return 'Unknown';
  }

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(new Date(value));
}

function actionLabel(action: AdminBonusAction) {
  const zone = action.zone?.name ?? action.zoneBonus?.zone?.name ?? 'Unknown zone';
  const bonus = displayBonusLabel(action.bonusType ?? action.zoneBonus?.bonusType) || 'Unknown bonus';

  switch (action.actionType) {
    case 'ADD':
      return `Added ${bonus} to ${zone}`;
    case 'REMOVE':
      return `Removed ${bonus} from ${zone}`;
    case 'FLAG_REMOVAL':
      return `Flagged ${zone} for removal`;
    case 'CONFIRM_REMOVAL':
      return `Confirmed removal for ${zone}`;
    case 'CANCEL_REMOVAL':
      return `Canceled removal for ${zone}`;
    case 'ADMIN_REMOVE':
      return `Force removed ${zone}`;
    case 'ADMIN_RESTORE':
      return `Restored ${zone}`;
    case 'REPORT':
      return `Reported an action for ${zone}`;
    case 'IMPORT':
      return `Imported ${zone}`;
    default:
      return `${action.actionType} on ${zone}`;
  }
}

function statusBadgeClass(status: BonusReportStatus) {
  switch (status) {
    case 'OPEN':
      return 'text-bg-danger';
    case 'RESOLVED':
      return 'text-bg-success';
    case 'DISMISSED':
      return 'text-bg-secondary';
    default:
      return 'text-bg-secondary';
  }
}

function ActionBadge({ type }: { type: string }) {
  const className = type === 'ADD'
    ? 'text-bg-success'
    : type.includes('REMOVE') || type === 'FLAG_REMOVAL'
      ? 'text-bg-warning'
      : type === 'REPORT'
        ? 'text-bg-danger'
        : 'text-bg-secondary';

  return <span className={`badge ${className}`}>{type.replace(/_/g, ' ')}</span>;
}

function getDefaultReportState(): ReportModerationState {
  return { note: '', timeoutDays: 7 };
}

export function AdminBonusesPage() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<ModerationTab>('open');
  const [reportStates, setReportStates] = useState<Record<string, ReportModerationState>>({});

  const moderationQuery = useQuery({
    queryKey: ['admin', 'bonuses'],
    queryFn: fetchAdminBonusModeration
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['admin', 'bonuses'] });
    await queryClient.invalidateQueries({ queryKey: ['bonuses'] });
  };

  const resolveReportMutation = useMutation({
    mutationFn: ({ reportId, note }: { reportId: string; note?: string }) => resolveBonusReport(reportId, note),
    onSuccess: invalidate
  });

  const dismissReportMutation = useMutation({
    mutationFn: ({ reportId, note }: { reportId: string; note?: string }) => dismissBonusReport(reportId, note),
    onSuccess: invalidate
  });

  const removeBonusMutation = useMutation({
    mutationFn: ({ bonusId, note }: { bonusId: string; note?: string }) => adminRemoveBonus(bonusId, note),
    onSuccess: invalidate
  });

  const restoreBonusMutation = useMutation({
    mutationFn: ({ bonusId, note }: { bonusId: string; note?: string }) => adminRestoreBonus(bonusId, note),
    onSuccess: invalidate
  });

  const timeoutUserMutation = useMutation({
    mutationFn: ({ userId, days, note }: { userId: string; days: number; note?: string }) => timeoutBonusUser(userId, days, note),
    onSuccess: invalidate
  });

  const banUserMutation = useMutation({
    mutationFn: ({ userId, note }: { userId: string; note?: string }) => banBonusUser(userId, note),
    onSuccess: invalidate
  });

  const reports = moderationQuery.data?.reports ?? [];
  const recentActions = moderationQuery.data?.recentActions ?? [];
  const users = moderationQuery.data?.users ?? [];

  const openReports = useMemo(() => reports.filter((report) => report.status === 'OPEN'), [reports]);
  const closedReports = useMemo(() => reports.filter((report) => report.status !== 'OPEN'), [reports]);
  const displayedReports = activeTab === 'open' ? openReports : closedReports;

  const pendingMutation = resolveReportMutation.isPending
    || dismissReportMutation.isPending
    || removeBonusMutation.isPending
    || restoreBonusMutation.isPending
    || timeoutUserMutation.isPending
    || banUserMutation.isPending;

  const reportedBonusIds = useMemo(() => {
    return new Set(openReports.map((report) => report.zoneBonusId).filter((id): id is string => Boolean(id)));
  }, [openReports]);

  function getReportState(reportId: string) {
    return reportStates[reportId] ?? getDefaultReportState();
  }

  function updateReportState(reportId: string, patch: Partial<ReportModerationState>) {
    setReportStates((current) => ({
      ...current,
      [reportId]: {
        ...getDefaultReportState(),
        ...current[reportId],
        ...patch
      }
    }));
  }

  function clearReportState(reportId: string) {
    setReportStates((current) => {
      const next = { ...current };
      delete next[reportId];
      return next;
    });
  }

  function renderReport(report: AdminBonusReport) {
    const action = report.action;
    const targetUser = action.actor;
    const zoneBonusId = report.zoneBonusId ?? action.zoneBonusId ?? undefined;
    const reportState = getReportState(report.id);
    const note = reportState.note.trim() || undefined;
    const timeoutDays = Math.min(Math.max(Math.trunc(reportState.timeoutDays || 7), 1), 365);
    const isOpen = report.status === 'OPEN';

    return (
      <div className="card shadow-sm" key={report.id}>
        <div className="card-body">
          <div className="d-flex flex-wrap align-items-start justify-content-between gap-3">
            <div className="flex-grow-1">
              <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                <ActionBadge type={action.actionType} />
                <span className={`badge ${statusBadgeClass(report.status)}`}>{report.status}</span>
                <span className="small text-body-secondary">Reported {formatDateTime(report.createdAt)}</span>
              </div>
              <h2 className="h5 mb-1">{actionLabel(action)}</h2>
              <p className="text-body-secondary mb-2">
                Reported by <strong>{report.reporter?.username ?? 'Unknown user'}</strong>
                {targetUser ? <> against <strong>{targetUser.username}</strong></> : null}
                {report.server ? <> on <strong>{report.server.name}</strong></> : null}.
              </p>
              {report.reason ? <p className="mb-1"><span className="fw-semibold">User report reason:</span> {report.reason}</p> : null}
              {action.reason ? <p className="small text-body-secondary mb-1"><span className="fw-semibold">Original action note:</span> {action.reason}</p> : null}
              {report.moderatorNote ? <p className="small text-body-secondary mb-0"><span className="fw-semibold">Moderator note:</span> {report.moderatorNote}</p> : null}
              {report.resolvedAt ? <p className="small text-body-secondary mb-0"><span className="fw-semibold">Closed:</span> {formatDateTime(report.resolvedAt)}</p> : null}
            </div>
          </div>

          {isOpen ? (
            <div className="border-top pt-3 mt-3">
              <div className="row g-3 align-items-end">
                <div className="col-lg-7">
                  <label className="form-label" htmlFor={`report-note-${report.id}`}>Moderator note</label>
                  <textarea
                    id={`report-note-${report.id}`}
                    className="form-control"
                    rows={2}
                    value={reportState.note}
                    onChange={(event) => updateReportState(report.id, { note: event.target.value })}
                  />
                </div>
                <div className="col-sm-6 col-lg-2">
                  <label className="form-label" htmlFor={`report-timeout-${report.id}`}>Timeout days</label>
                  <input
                    id={`report-timeout-${report.id}`}
                    className="form-control"
                    type="number"
                    min={1}
                    max={365}
                    value={reportState.timeoutDays}
                    onChange={(event) => updateReportState(report.id, { timeoutDays: Number(event.target.value) })}
                  />
                </div>
                <div className="col-lg-3">
                  <div className="d-flex flex-wrap gap-2 justify-content-lg-end">
                    {zoneBonusId ? (
                      <button className="btn btn-outline-danger btn-sm" type="button" onClick={() => removeBonusMutation.mutate({ bonusId: zoneBonusId, note })} disabled={pendingMutation}>
                        Force remove
                      </button>
                    ) : null}
                    <button
                      className="btn btn-success btn-sm"
                      type="button"
                      onClick={() => resolveReportMutation.mutate({ reportId: report.id, note }, { onSuccess: () => clearReportState(report.id) })}
                      disabled={pendingMutation}
                    >
                      Resolve
                    </button>
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      type="button"
                      onClick={() => dismissReportMutation.mutate({ reportId: report.id, note }, { onSuccess: () => clearReportState(report.id) })}
                      disabled={pendingMutation}
                    >
                      Dismiss
                    </button>
                    {targetUser ? (
                      <>
                        <button className="btn btn-outline-warning btn-sm" type="button" onClick={() => timeoutUserMutation.mutate({ userId: targetUser.id, days: timeoutDays, note })} disabled={pendingMutation}>
                          Timeout
                        </button>
                        <button className="btn btn-outline-danger btn-sm" type="button" onClick={() => banUserMutation.mutate({ userId: targetUser.id, note })} disabled={pendingMutation}>
                          Ban
                        </button>
                      </>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  function renderAction(action: AdminBonusAction) {
    const bonusId = action.zoneBonusId ?? action.zoneBonus?.id ?? null;
    const isReported = bonusId ? reportedBonusIds.has(bonusId) : false;
    const isRemoved = action.zoneBonus?.state === 'REMOVED';

    return (
      <tr key={action.id}>
        <td className="text-nowrap">{formatDateTime(action.createdAt)}</td>
        <td><ActionBadge type={action.actionType} /></td>
        <td>{action.server?.name ?? action.serverId}</td>
        <td>{action.zone?.name ?? action.zoneBonus?.zone?.name ?? 'Unknown zone'}</td>
        <td>{action.bonusType?.label ?? action.zoneBonus?.bonusType?.label ?? '-'}</td>
        <td>{action.actor?.username ?? 'System'}</td>
        <td>{action.reason ?? '-'}</td>
        <td>
          <div className="d-flex flex-wrap gap-2">
            {bonusId && isRemoved ? (
              <button className="btn btn-outline-success btn-sm" type="button" onClick={() => restoreBonusMutation.mutate({ bonusId })} disabled={pendingMutation}>
                Restore
              </button>
            ) : null}
            {bonusId && !isRemoved ? (
              <button className="btn btn-outline-danger btn-sm" type="button" onClick={() => removeBonusMutation.mutate({ bonusId })} disabled={pendingMutation}>
                Remove
              </button>
            ) : null}
            {isReported ? <span className="badge text-bg-danger align-self-center">Reported</span> : null}
          </div>
        </td>
      </tr>
    );
  }

  function renderUser(user: AdminReportedUser) {
    return (
      <tr key={user.id}>
        <td>{user.username}</td>
        <td><span className="badge text-bg-secondary">{user.role}</span></td>
        <td>{user.openReportCount}</td>
      </tr>
    );
  }

  return (
    <div className="container-fluid px-3 px-lg-4 py-4">
      <div className="d-flex flex-wrap align-items-end justify-content-between gap-3 mb-4">
        <div>
          <h1 className="h2 mb-1">Bonus Moderation</h1>
          <p className="text-body-secondary mb-0">Review reported Resource Hunter actions and quickly correct bad bonus data.</p>
        </div>
        <button className="btn btn-outline-secondary btn-sm" type="button" onClick={() => void moderationQuery.refetch()} disabled={moderationQuery.isFetching}>
          <i className="bi bi-arrow-clockwise me-1" /> Refresh
        </button>
      </div>

      {moderationQuery.isLoading ? <LoadingState label="Loading bonus moderation" /> : null}
      {moderationQuery.isError ? <ErrorState title="Could not load bonus moderation" error={moderationQuery.error} /> : null}

      {moderationQuery.data ? (
        <div className="d-flex flex-column gap-4">
          <section>
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
              <h2 className="h4 mb-0">Reports</h2>
              <ul className="nav nav-pills small">
                <li className="nav-item">
                  <button className={`nav-link ${activeTab === 'open' ? 'active' : ''}`} type="button" onClick={() => setActiveTab('open')}>
                    Open <span className="badge text-bg-secondary ms-1">{openReports.length}</span>
                  </button>
                </li>
                <li className="nav-item">
                  <button className={`nav-link ${activeTab === 'closed' ? 'active' : ''}`} type="button" onClick={() => setActiveTab('closed')}>
                    Closed <span className="badge text-bg-secondary ms-1">{closedReports.length}</span>
                  </button>
                </li>
              </ul>
            </div>
            {displayedReports.length > 0 ? <div className="d-flex flex-column gap-3">{displayedReports.map(renderReport)}</div> : (
              <div className={`alert ${activeTab === 'open' ? 'alert-success' : 'alert-secondary'} mb-0`}>
                {activeTab === 'open' ? 'No open bonus reports.' : 'No closed bonus reports yet.'}
              </div>
            )}
          </section>

          <section>
            <h2 className="h4 mb-3">Reported Users</h2>
            <div className="card shadow-sm">
              <div className="table-responsive">
                <table className="table table-dark table-hover align-middle mb-0">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Role</th>
                      <th>Open reports</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.length > 0 ? users.map(renderUser) : (
                      <tr><td colSpan={3} className="text-body-secondary">No users currently have open reports.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          <section>
            <h2 className="h4 mb-3">Recent Activity</h2>
            <div className="card shadow-sm">
              <div className="table-responsive">
                <table className="table table-dark table-hover align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Type</th>
                      <th>Server</th>
                      <th>Zone</th>
                      <th>Bonus</th>
                      <th>User</th>
                      <th>Reason</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>{recentActions.map(renderAction)}</tbody>
                </table>
              </div>
            </div>
          </section>
        </div>
      ) : null}
    </div>
  );
}
